import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getLeaderboard,
  searchLeaderboard,
  getUnownedPrediction,
  forceRemovePredictionFromGroup,
} from "../../../services/predictionsService";
import LoadingContext from "../../../context/loadingContext";
import LeaderboardTable from "./leaderboardTable";
import LeaderboardViewPredictionModal from "./leaderboardViewPredictionModal";
import LeaderboardInviteModal from "./leaderboardInviteModal";
import { getMatches } from "../../../services/matchService";
import { getCompetition } from "../../../services/competitionService";
import PageSelection from "../../common/pageSections/pageSelection";
import TeamSelectComponent from "../maker/teamSelectComponent";
import { getResult, getWhatIfResult } from "../../../services/resultsService";
import Confirm from "../../common/modal/confirm";
import BonusPickInfo from "./bonusPickInfo";
import IconRender from "../../common/icons/iconRender";
import DualNavLink from "../../common/pageSections/dualNavLink";
import { submissionDeadlinePassed } from "../../../utils/competitionsUtil";
import { filterRealTeams } from "../../../utils/predictionsUtil";
import LeaderboardTeamEliminationsModal from "./leaderboardTeamEliminationsModal";
import LeaderboardWhatIfModal from "./leaderboardWhatIfModal";

const PredictionsLeaderboard = ({ competitionID, groupID, isSecondChance }) => {
  let navigate = useNavigate();
  const { setLoading, user } = useContext(LoadingContext);
  const [leaderboard, setLeaderboard] = useState([]);
  const [groupInfo, setGroupInfo] = useState(null);
  const [competition, setCompetition] = useState([]);
  const [originalMatches, setOriginalMatches] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [singlePredictionOpen, setSinglePredictionOpen] = useState(false);
  const [predictionCount, setPredictionCount] = useState(0);
  const [page, setPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(
    groupID === "all" ? 50 : 100,
  );
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);
  const [forceRemoveOpen, setForceRemoveOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [bonusPicksOpen, setBonusPicksOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamEliminationsOpen, setTeamEliminationsOpen] = useState(false);
  const [teamEliminations, setTeamEliminations] = useState([]);
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [whatIfData, setWhatIfData] = useState(null);

  const loadLeaderboard = async (
    selectedPage,
    updatedResultsPerPage,
    search,
  ) => {
    setLoading(true);
    let leaderboardRes;
    if (search) {
      setSearched(true);
      leaderboardRes = await searchLeaderboard(
        competitionID,
        groupID,
        search,
        isSecondChance,
      );
    } else {
      setSearched(false);
      leaderboardRes = await getLeaderboard(
        competitionID,
        selectedPage || page,
        updatedResultsPerPage || resultsPerPage,
        groupID,
        isSecondChance,
      );
    }
    if (leaderboardRes.status === 200) {
      setPage(selectedPage || page);
      setResultsPerPage(updatedResultsPerPage || resultsPerPage);
      setLeaderboard(leaderboardRes.data.predictions);
      setPredictionCount(leaderboardRes.data.count);
      setGroupInfo(leaderboardRes.data.groupInfo);
    } else toast.error(leaderboardRes.data);
    setLoading(false);
  };

  const loadData = async () => {
    setLoading(true);
    const matchesRes = await getMatches(competitionID);
    const competitionRes = await getCompetition(competitionID);
    const resultRes = await getResult(competitionID);

    if (matchesRes.status) {
      let playoffMatches = [];
      let allTeams = [];
      matchesRes.data.forEach((m) => {
        if (m.type === "Playoff") playoffMatches.push(m);
        if (!allTeams.includes(m.homeTeamName)) allTeams.push(m.homeTeamName);
        if (!allTeams.includes(m.awayTeamName)) allTeams.push(m.awayTeamName);
      });
      setOriginalMatches(playoffMatches);
      // Filter to teams with a logo entry — matches data includes placeholder
      // names (e.g. "Winner Group A") that should not appear in the team picker.
      // Every real competing nation must have a logo; if a team is missing one,
      // add it to logos.js rather than relaxing this filter.
      setAllTeams(filterRealTeams(allTeams));
      if (competitionRes.status === 200) {
        setCompetition(competitionRes.data);
        if (resultRes && resultRes.status === 200) {
          setResult(resultRes.data);
          // do not give error here, results may not exist yet
        }
        const whatIfRes = await getWhatIfResult(competitionRes.data.code);
        if (whatIfRes && whatIfRes.status === 200)
          setWhatIfData(whatIfRes.data);
      } else toast.error(competitionRes.data);
    } else toast.error(matchesRes.data);

    loadLeaderboard();
  };

  useEffect(() => {
    loadData();
  }, [isSecondChance]);

  const handleSelectPrediction = async (prediction) => {
    if (!prediction) return;

    if (new Date(competition.submissionDeadline) > new Date()) {
      setSelectedPrediction(prediction);
      setSinglePredictionOpen(true);
    } else {
      setLoading(true);
      const res = await getUnownedPrediction(prediction._id);
      if (res.status === 200) {
        setSelectedPrediction(res.data);
        setSinglePredictionOpen(true);
      } else if (res.data.toLowerCase().includes("token"))
        toast.error("You must be logged in to view individual submissions");
      else toast.error(res.data);
    }

    setLoading(false);
  };

  const handleForceRemovePrediction = async (prediction) => {
    setLoading(true);
    const res = await forceRemovePredictionFromGroup(prediction._id, groupInfo);
    if (res.status === 200) {
      toast.success(`${prediction.name} has been removed from your group`);
      setSinglePredictionOpen(false);
      return loadLeaderboard();
    } else toast.error(res.data);
    setLoading(false);
  };

  return (
    <div className="page-container">
      <DualNavLink
        left={{
          label: "Your Submissions",
          onClick: () =>
            navigate(`/submissions?competitionID=${competitionID}`),
        }}
      />
      <div className="standout-header">
        {competition.name}
        {isSecondChance && (
          <div style={{ fontSize: "0.65em", fontWeight: "normal" }}>
            Second Chance
          </div>
        )}
      </div>
      <div>
        <div className="standout-header sub">
          {(groupInfo || { name: "Sitewide" })?.name} Leaderboard
        </div>
        {groupInfo?.ownerID?.name && (
          <div>
            Group Owner: <b>{groupInfo?.ownerID?.name}</b>
          </div>
        )}
        {competition.name && (
          <div className="fine-print">
            {competition.lastCalculated
              ? `Last calculated: ${new Date(competition.lastCalculated).toLocaleString()}`
              : "Standings not yet calculated"}
          </div>
        )}
        {groupInfo?.ownerID && groupInfo?.ownerID._id === user?._id && (
          <button
            className="btn btn-sm btn-dark"
            onClick={() => setInviteOpen(true)}
          >
            Invite Users
          </button>
        )}
        <div className="btn-toolbar">
          {!result?.misc?.winner && !groupInfo && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setWhatIfOpen(true)}
            >
              Final Scenarios
            </button>
          )}
          {allTeams.length > 0 && submissionDeadlinePassed(competition) && (
            <TeamSelectComponent
              teams={allTeams.map((t) => ({ value: t }))}
              onSelect={(team) => {
                setSelectedTeam(team);
                setTeamEliminations([]);
                setTeamEliminationsOpen(true);
              }}
              title="Team Eliminations"
              subtitle={`Select a team to view the breakdown of predicted elimination rounds across all ${groupInfo ? groupInfo.name + " " : ""}submissions`}
              compact
            />
          )}
          {!isSecondChance && !!result?.leaders?.length && (
            <button
              className="btn btn-small btn-light"
              onClick={() => setBonusPicksOpen(true)}
            >
              Bonus Pick Leaders
            </button>
          )}
        </div>
      </div>
      {competition.secondChance && !groupInfo && (
        <span
          key="switch"
          className="view-switch-link"
          onClick={() =>
            navigate(
              `/competitions?leaderboard=show&competitionID=${competitionID}&groupID=${groupID}&secondChance=${!isSecondChance}`,
              { replace: true },
            )
          }
        >
          {isSecondChance && <IconRender type="left" size={13} />} View{" "}
          {isSecondChance ? "Full" : "Second Chance"} Leaderboard{" "}
          {!isSecondChance && <IconRender type="right" size={13} />}
        </span>
      )}
      <LeaderboardTable
        leaderboard={leaderboard}
        onSelectPrediction={handleSelectPrediction}
        onForceRemovePrediction={handleForceRemovePrediction}
        groupInfo={groupInfo}
        onSearch={(value) => loadLeaderboard(null, null, value)}
        setForceRemoveOpen={setForceRemoveOpen}
        setSelectedPrediction={setSelectedPrediction}
        isSecondChance={isSecondChance}
      />
      {leaderboard.length === 0 && (
        <div className="single-card text-center">
          <b>
            No submissions found{searched ? " using the search terms" : ""}.
          </b>
        </div>
      )}
      {!isSecondChance && (
        <BonusPickInfo
          isOpen={bonusPicksOpen}
          setIsOpen={setBonusPicksOpen}
          result={result}
          competition={competition}
          setSinglePredictionOpen={setSinglePredictionOpen}
          setSelectedPrediction={setSelectedPrediction}
        />
      )}
      {!searched && leaderboard.length > 0 && (
        <PageSelection
          totalCount={predictionCount}
          displayPerPage={resultsPerPage}
          pageNumber={page}
          onSelectPage={(page) => loadLeaderboard(page)}
          onClickCaret={(movement) => loadLeaderboard(page + movement)}
          resultsSelectionArray={[50, 100, 250]}
          onUpdateResultsPerPage={(selection) => loadLeaderboard(1, selection)}
        />
      )}
      <div className="fine-print" style={{ marginTop: "24px" }}>
        <b>Potential Points (pot)</b>: the maximum total points a submission
        could finish with if every remaining pick goes their way. Only
        calculated once the group stage is complete.
        <ul>
          <li>
            <b>Realistic</b>: bonus picks (e.g. top scorer, discipline) are only
            counted if that team/player is still in contention (e.g. within a
            couple goals of the top scorer, or only a few cards behind on
            discipline).
          </li>
          <li>
            <b>Maximum</b>: bonus picks are counted as long as that team/player
            is still in the tournament, regardless of how far behind they are.
          </li>
        </ul>
      </div>
      {selectedPrediction && (
        <LeaderboardViewPredictionModal
          prediction={selectedPrediction}
          isOpen={singlePredictionOpen}
          setIsOpen={setSinglePredictionOpen}
          originalMatches={originalMatches}
          competition={competition}
          allTeams={allTeams}
          result={result}
          setForceRemoveOpen={setForceRemoveOpen}
          setSelectedPrediction={setSelectedPrediction}
          groupInfo={groupInfo}
          isSecondChance={isSecondChance}
        />
      )}
      {selectedPrediction && (
        <Confirm
          header="Confirm Remove Prediction"
          isOpen={forceRemoveOpen}
          setIsOpen={() => setForceRemoveOpen(false)}
          focus="cancel"
          onConfirm={() => handleForceRemovePrediction(selectedPrediction)}
        >
          <b>{selectedPrediction.name}</b>
          <br />
          Are you sure you want to remove this submission from your group?
          <br />
          If you do not want the user to be able to re-add their prediction to
          this group you should change the group passcode.
        </Confirm>
      )}
      <LeaderboardWhatIfModal
        isOpen={whatIfOpen}
        setIsOpen={setWhatIfOpen}
        whatIfData={whatIfData}
        isSecondChance={isSecondChance}
      />
      <LeaderboardTeamEliminationsModal
        isOpen={teamEliminationsOpen}
        setIsOpen={(val) => {
          setTeamEliminationsOpen(val);
          if (!val) setSelectedTeam(null);
        }}
        team={selectedTeam}
        competitionID={competitionID}
        groupID={groupID}
        groupName={groupInfo?.name}
        isSecondChance={isSecondChance}
        eliminations={teamEliminations}
        onSetEliminations={setTeamEliminations}
      />
      {groupInfo && (
        <LeaderboardInviteModal
          isOpen={inviteOpen}
          setIsOpen={setInviteOpen}
          group={groupInfo}
          competition={competition}
        />
      )}
    </div>
  );
};

export default PredictionsLeaderboard;
